import React from 'react';
import { ToastContainer as ReactToastContainer, Slide, toast, TypeOptions } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import styled from 'styled-components';

import { ErrorType } from '@ndvr/customer-api';

import { copyText } from 'utils/dom-utils';
import { convertErrorType } from 'utils/error-utils';

import { addRumError } from '../datadog-logging';
import { Button } from './button';
import { HorizontalLayout, VerticalLayout } from './Layout';
import { Text } from './Text';
import { Tooltip } from './Tooltip';

type ToastContent = JSX.Element | string;

export const ToastContainer = () => (
    <StyledToastContainer
        position={toast.POSITION.TOP_CENTER}
        closeButton={false}
        closeOnClick={true}
        draggable={false}
        enableMultiContainer={false}
        limit={3}
        icon={false}
        hideProgressBar
        transition={Slide}
    />
);

export const Toast = {
    dismiss: toast.dismiss,
    success: (text: string) => {
        return toast(<b>🎉 {text}</b>, {
            theme: 'colored',
            autoClose: 1000,
            type: 'success',
        });
    },
    info: (text: string) => {
        return toast(<b>{text}</b>, {
            theme: 'colored',
            autoClose: 1000,
            type: 'info',
        });
    },
    forError: (err: any, message?: string) => {
        addRumError(err, {
            errorMessageApp: message,
            errorId: err.errorId,
            errorType: err.errorType,
            errorMesageBackend: err.backendMessage ?? err.message,
            httpStatus: err.httpStatus,
            url: err.url,
        });

        return Toast.error(
            <>
                <VerticalLayout $gap={8}>
                    {err.errorType && (
                        <Text>
                            <b>{convertErrorType(err.errorType)}</b>
                        </Text>
                    )}
                    {message && <Text>{message}</Text>}
                    {err.message && err.message !== message && <Text helper>{err.message}</Text>}
                </VerticalLayout>
                {err.errorId && (
                    <ToastFooter $apart $rowCenter>
                        <Text helper style={{ fontFamily: 'monospace', fontSize: '0,6875rem' }}>
                            error Id #{err.errorId}
                        </Text>
                        <StyledButton
                            size="small"
                            buttonType="destructive"
                            onClick={() => void copyText(`Error Id: #${err.errorId}`)}
                        >
                            <Tooltip tooltip="For faster support, please include this error ID when contacting us.">
                                <Text helper>Copy to clipboard</Text>
                            </Tooltip>
                        </StyledButton>
                    </ToastFooter>
                )}
            </>,
            err.errorType === ErrorType.EX_GENERIC ? 'error' : 'info',
        );
    },
    error: (children: ToastContent, toastType: TypeOptions): void => {
        toast(children, {
            closeButton: true,
            autoClose: false,
            closeOnClick: false,
            theme: 'colored',
            type: toastType,
        });
    },
};

const ToastFooter = styled(HorizontalLayout)`
    padding: 0 0 0 8px;
    background-color: rgba(255, 255, 255, 0.166);
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    & span {
        margin: 0;
    }
`;

const StyledButton = styled(Button)`
    border-radius: 0;
    border-width: 0;
    padding: 4px 12px 4px 12px;

    color: ${({ theme }) => theme.text.white};

    &:hover {
        color: ${({ theme }) => theme.text.negative};
    }
`;

const StyledToastContainer = styled(ReactToastContainer)`
    width: 480px;
    --toastify-toast-width: 480px;

    .Toastify__toast {
        word-break: break-word;
        border-radius: 3px;
        color: ${({ theme }) => theme.text.white};
    }
    .Toastify__toast-body {
        padding: 0;
        overflow: auto;
    }
    .Toastify__toast--error {
        position: relative;
        padding-bottom: 36px;
    }
    .Toastify__toast--info {
        position: relative;
        padding-bottom: 36px;
    }

    --toastify-color-info: ${({ theme }) => theme.colors.gold400};
    --toastify-color-success: ${({ theme }) => theme.colors.cocoa900};
    --toastify-color-error: ${({ theme }) => theme.intent.negative};
    --toastify-toast-min-height: 0;
`;
